import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, ArrowRight, Flame, Bell } from 'lucide-react';
import { productsApi } from '../../lib/api';
import { NewArrivalsStrip } from '../shop/NewArrivalsStrip';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const imgSrc = (url) => (url && url.startsWith('/') ? `${API_URL}${url}` : url);

/**
 * Homepage merch showcase — City Series drop (live), HYROX competition
 * apparel (coming soon) and shop bestsellers, so the store is visible
 * without leaving the homepage.
 */
export default function MerchShowcase() {
  const [tees, setTees] = useState([]);
  const [womens, setWomens] = useState(null);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [csRes, wRes, pRes] = await Promise.all([
          fetch(`${API_URL}/api/city-series`).then((r) => r.json()),
          fetch(`${API_URL}/api/womens-collection`).then((r) => r.json()),
          productsApi.getAll(),
        ]);
        if (cancelled) return;
        setTees((csRes.items || []).slice(0, 4));
        setWomens(wRes);
        setProducts(Array.isArray(pRes.data) ? pRes.data : []);
      } catch { /* section auto-hides */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const hyrox = products.filter((p) => p.coming_soon);
  const bestsellers = products.filter((p) => !p.coming_soon).slice(0, 4);

  if (tees.length === 0 && products.length === 0) return null;

  return (
    <section className="relative bg-black border-t border-b border-zinc-900" data-testid="merch-showcase">
      <div className="container-ninja py-14 md:py-20">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <p className="text-[#C7FA3E] text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2" style={{ fontFamily: 'Oswald, sans-serif' }}>
              <ShoppingBag className="w-3.5 h-3.5" /> The Store
            </p>
            <h2 className="text-white text-3xl sm:text-4xl" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '0.02em' }}>
              Wear the Work
            </h2>
          </div>
          <Link to="/shop" className="inline-flex items-center gap-1.5 text-[#C7FA3E] text-xs uppercase tracking-widest hover:gap-2.5 transition-all" style={{ fontFamily: 'Oswald, sans-serif' }} data-testid="merch-showcase-shop-link">
            Shop everything <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        <NewArrivalsStrip />

        {/* City Series — live drop */}
        {tees.length > 0 && (
          <div className="mb-12" data-testid="merch-city-series-block">
            <div className="flex items-center gap-3 mb-4">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] uppercase tracking-wider bg-[#E63946] text-white font-bold">
                <Flame className="w-3 h-3" /> Drop 01 · Live now
              </span>
              <h3 className="text-white text-xl" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '0.02em' }}>City Series Run Club Tees</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {tees.map((t) => (
                <Link key={t.key} to="/city-series" className="group block" data-testid={`merch-city-tee-${t.key}`}>
                  <div className="aspect-square overflow-hidden bg-zinc-900 border border-zinc-800 group-hover:border-[#C7FA3E]/50 transition-colors">
                    <img src={imgSrc(t.image)} alt={t.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                  </div>
                  <p className="text-white text-sm mt-2" style={{ fontFamily: 'Oswald, sans-serif' }}>{t.city} · ${Math.round(t.price)}</p>
                </Link>
              ))}
            </div>
            <Link to="/city-series" className="inline-flex items-center gap-1.5 mt-4 text-zinc-400 text-xs uppercase tracking-widest hover:text-[#C7FA3E] transition-colors" style={{ fontFamily: 'Oswald, sans-serif' }} data-testid="merch-city-series-link">
              All 14 cities <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        )}

        {/* Women's Collection — Drop 02 */}
        {womens?.items?.length > 0 && (
          <div className="mb-12" data-testid="merch-womens-block">
            <div className="flex items-center gap-3 mb-4">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] uppercase tracking-wider bg-[#C7FA3E] text-black font-bold">
                <Bell className="w-3 h-3" /> {womens.status === 'live' ? 'Drop 02 · Live now' : 'Drop 02 · Waitlist open'}
              </span>
              <h3 className="text-white text-xl" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '0.02em' }}>Women&apos;s Collection</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {womens.items.map((w) => (
                <Link key={w.key} to="/womens-collection" className="group block" data-testid={`merch-womens-${w.key}`}>
                  <div className="aspect-square overflow-hidden bg-zinc-900 border border-zinc-800 group-hover:border-[#C7FA3E]/50 transition-colors">
                    <img src={imgSrc(w.image)} alt={w.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                  </div>
                  <p className="text-white text-sm mt-2" style={{ fontFamily: 'Oswald, sans-serif' }}>{w.name} · ${Math.round(w.price)}</p>
                </Link>
              ))}
            </div>
            <Link to="/womens-collection" className="inline-flex items-center gap-1.5 mt-4 text-zinc-400 text-xs uppercase tracking-widest hover:text-[#C7FA3E] transition-colors" style={{ fontFamily: 'Oswald, sans-serif' }} data-testid="merch-womens-link">
              {womens.status === 'live' ? 'Shop the drop' : 'Join the waitlist'} <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-10">
          {/* HYROX competition apparel — coming soon */}
          {hyrox.length > 0 && (
            <div data-testid="merch-hyrox-block">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-2.5 py-1 text-[11px] uppercase tracking-wider bg-[#C7FA3E] text-black font-bold">Coming Soon</span>
                <h3 className="text-white text-xl" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '0.02em' }}>HYROX Competition Apparel</h3>
              </div>
              <div className="grid grid-cols-3 gap-4">
                {hyrox.slice(0, 3).map((p) => (
                  <Link key={p.slug} to="/coming-soon" className="group block" data-testid={`merch-hyrox-${p.slug}`}>
                    <div className="aspect-square overflow-hidden bg-zinc-900 border border-zinc-800 group-hover:border-[#C7FA3E]/50 transition-colors">
                      <img src={imgSrc(p.images?.[0])} alt={p.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                    </div>
                    <p className="text-zinc-300 text-xs mt-2 leading-snug">{p.name}</p>
                  </Link>
                ))}
              </div>
              <Link to="/coming-soon" className="inline-flex items-center gap-1.5 mt-4 text-zinc-400 text-xs uppercase tracking-widest hover:text-[#C7FA3E] transition-colors" style={{ fontFamily: 'Oswald, sans-serif' }} data-testid="merch-hyrox-waitlist-link">
              <Bell className="w-3.5 h-3.5" /> Join the waitlist <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            </div>
          )}

          {/* Bestsellers */}
          {bestsellers.length > 0 && (
            <div data-testid="merch-bestsellers-block">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-2.5 py-1 text-[11px] uppercase tracking-wider border border-zinc-700 text-zinc-300">Bestsellers</span>
                <h3 className="text-white text-xl" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '0.02em' }}>Training Essentials</h3>
              </div>
              <div className="grid grid-cols-4 gap-3">
                {bestsellers.map((p) => (
                  <Link key={p.slug} to={`/shop/${p.slug}`} className="group block" data-testid={`merch-bestseller-${p.slug}`}>
                    <div className="aspect-square overflow-hidden bg-zinc-900 border border-zinc-800 group-hover:border-[#C7FA3E]/50 transition-colors">
                      <img src={imgSrc(p.images?.[0])} alt={p.name} className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                    </div>
                    <p className="text-zinc-300 text-xs mt-2 leading-snug truncate">{p.name}</p>
                    <p className="text-[#C7FA3E] text-xs">${Math.round(p.price)}</p>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
